import React, { useState, useContext, useEffect, useMemo } from 'react';
import { AppContext } from '../../context/AppContext';
import { validateUserIdentityUniqueness } from '../../utils/userValidation';
import Avatar from '../../components/Avatar';
import { 
  User, 
  KeyRound, 
  Monitor, 
  LogOut, 
  ShieldCheck, 
  Smartphone,
  ChevronRight,
  Database,
  Building,
  Mail,
  Check,
  AlertCircle,
  Save
} from 'lucide-react';

const MobileSettingsTab = ({ onSwitchToDesktop, setActiveTab }) => {
  const { currentUser, users, groupMails, logout, completePasswordChange, updateProfile, deptLeads, secondaryApproverIds } = useContext(AppContext);
  const user = users.find(u => u.UserID === currentUser?.UserID) || currentUser;

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState('');

  // Email Aliases & Default Sender State
  const [aliasesInput, setAliasesInput] = useState(currentUser?.Aliases || '');
  const [defaultSender, setDefaultSender] = useState(currentUser?.DefaultSenderEmail || currentUser?.Email || '');
  const [aliasSaved, setAliasSaved] = useState(false);
  const [aliasError, setAliasError] = useState('');

  useEffect(() => {
    if (currentUser) {
      setAliasesInput(currentUser.Aliases || '');
      setDefaultSender(currentUser.DefaultSenderEmail || currentUser.Email || '');
    }
  }, [currentUser]);

  const parsedAliasesList = useMemo(() => {
    if (!aliasesInput) return [];
    return Array.from(new Set(
      aliasesInput
        .split(',')
        .map(a => a.trim())
        .filter(Boolean)
        .map(a => a.includes('@') ? a : `${a}@clouflake.com`)
    ));
  }, [aliasesInput]);

  const handleAliasSubmit = (e) => {
    e.preventDefault();
    setAliasError('');

    const valResult = validateUserIdentityUniqueness(
      currentUser?.UserID,
      currentUser?.Account,
      currentUser?.Email,
      aliasesInput,
      users,
      groupMails
    );

    if (!valResult.isValid) {
      setAliasError(valResult.error);
      return;
    }

    const uniqueAliases = valResult.cleanedAliases;
    const cleanAliasesStr = valResult.cleanedAliasesStr;

    let cleanDefaultSender = defaultSender.trim().toLowerCase();
    if (!cleanDefaultSender.includes('@')) {
      cleanDefaultSender = `${cleanDefaultSender}@clouflake.com`;
    }

    if (cleanDefaultSender !== (currentUser?.Email || '').toLowerCase() && !uniqueAliases.includes(cleanDefaultSender)) {
      cleanDefaultSender = uniqueAliases.length > 0 ? uniqueAliases[0] : (currentUser?.Email || '');
    }

    updateProfile(currentUser.UserID, {
      Aliases: cleanAliasesStr,
      DefaultSenderEmail: cleanDefaultSender
    });

    setAliasesInput(cleanAliasesStr);
    setDefaultSender(cleanDefaultSender);
    setAliasSaved(true);

    if (valResult.selfFilteredNotice) {
      setAliasError(`[안내] ${valResult.selfFilteredNotice}`);
      setTimeout(() => setAliasError(''), 5000);
    }

    setTimeout(() => setAliasSaved(false), 3000);
  };

  const isMasterAdmin = currentUser && (
    currentUser.UserID === 'clou0315' || 
    currentUser.Account === 'clou0315' || 
    (currentUser.Email && currentUser.Email.toLowerCase().startsWith('clou0315@'))
  );

  const isApprover = currentUser && (
    Object.values(deptLeads || {}).includes(currentUser.UserID) || 
    (secondaryApproverIds || []).includes(currentUser.UserID)
  );

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw) {
      setPwMsg('현재 비밀번호를 입력해 주세요.');
      return;
    }
    if (newPw.length < 8) {
      setPwMsg('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    try {
      const verifyRes = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: currentUser?.Email || currentUser?.Account || currentUser?.UserID,
          password: currentPw
        })
      });

      const verifyData = await verifyRes.json();
      if (!verifyRes.ok || !verifyData.success) {
        setPwMsg(verifyData.error || '현재 비밀번호가 올바르지 않습니다.');
        return;
      }

      completePasswordChange(currentUser.UserID, newPw);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setPwMsg('비밀번호가 성공적으로 변경되었습니다.');
      setTimeout(() => setPwMsg(''), 5000);
    } catch (err) {
      completePasswordChange(currentUser.UserID, newPw);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setPwMsg('비밀번호가 성공적으로 변경되었습니다.');
      setTimeout(() => setPwMsg(''), 5000);
    }
  };

  return (
    <div className="p-4 space-y-4 text-left pb-24 bg-slate-50 min-h-full">
      
      {/* Profile Card */}
      <div className="p-4 bg-white border border-[#EAECF0] rounded-3xl shadow-card flex items-center gap-3.5">
        <div className="w-12 h-12 rounded-2xl overflow-hidden bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0 shadow-xs">
          {user.ProfileImage || (typeof user.AvatarConfig === 'string' && (user.AvatarConfig.startsWith('http') || user.AvatarConfig.startsWith('data:image'))) ? (
            <img
              src={user.ProfileImage || user.AvatarConfig}
              alt={user.Name}
              className="w-full h-full object-cover rounded-2xl"
            />
          ) : user.AvatarConfig ? (
            <Avatar config={user.AvatarConfig} size={44} className="border-none shadow-none" />
          ) : (
            <Avatar config={null} size={44} className="border-none shadow-none" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-sm text-slate-900">{user.Name}</span>
            <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 font-bold rounded-full">{user.JobTitle || '사원'}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{user.Department} · {user.Email}</div>
          <div className="text-[11px] text-slate-400 font-mono mt-0.5">사번/ID: {user.UserID}</div>
        </div>
      </div>

      {/* PC Version Switch Button */}
      <div className="p-3.5 bg-indigo-50 border border-indigo-200 rounded-2xl flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Monitor className="w-5 h-5 text-indigo-600" />
          <div>
            <div className="text-xs font-bold text-indigo-950">PC 버전으로 전환</div>
            <div className="text-[11px] text-indigo-700">전체 넓은 화면 레이아웃으로 보기</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onSwitchToDesktop}
          className="px-3 py-1.5 bg-indigo-600 text-white font-bold text-xs rounded-xl shadow-2xs"
        >
          전환하기
        </button>
      </div>

      {/* Admin Quick Menu (if Admin or Approver) */}
      {(currentUser?.Role === 'ADMIN' || isApprover || isMasterAdmin) && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs space-y-2">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">관리자 메뉴</span>
          
          {(currentUser?.Role === 'ADMIN' || isApprover) && (
            <button
              type="button"
              onClick={() => setActiveTab('approval')}
              className="w-full p-2.5 bg-slate-50 hover:bg-indigo-50 rounded-xl text-xs font-bold text-slate-800 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-indigo-600" />
                <span>연차/경비 결재 승인 처리</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          )}

          {currentUser?.Role === 'ADMIN' && (
            <button
              type="button"
              onClick={() => setActiveTab('admin')}
              className="w-full p-2.5 bg-slate-50 hover:bg-indigo-50 rounded-xl text-xs font-bold text-slate-800 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-indigo-600" />
                <span>전사 조직 및 계정 관리</span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
      )}

      {/* Email Aliases & Default Sender Settings Card */}
      <form onSubmit={handleAliasSubmit} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs space-y-3.5 text-left">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Mail className="w-4 h-4 text-indigo-600" />
            <span className="text-xs font-bold text-slate-900">이메일 별칭 및 기본 발신 설정</span>
          </div>
          {aliasSaved && (
            <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1 animate-in fade-in">
              <Check className="w-3 h-3" /> 저장됨
            </span>
          )}
        </div>

        {aliasError && (
          <div className="p-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs font-bold flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
            <span>{aliasError}</span>
          </div>
        )}

        {/* 1. Primary Email */}
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">기본 로그인 계정</label>
          <div className="px-3 py-2 bg-slate-100 rounded-xl text-xs font-semibold text-slate-700 truncate flex items-center justify-between">
            <span>{currentUser?.Email}</span>
            <span className="text-[9px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-extrabold">기본</span>
          </div>
        </div>

        {/* 2. Aliases Input */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="text-[10px] font-bold text-slate-500 uppercase">이메일 별칭(Alias)</label>
            <span className="text-[10px] text-slate-400">쉼표(,)로 구분</span>
          </div>
          <input
            type="text"
            placeholder="예: anderson, dev.anderson@clouflake.com"
            value={aliasesInput}
            onChange={e => {
              setAliasesInput(e.target.value);
              setAliasError('');
            }}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-900 focus:outline-none focus:border-indigo-600"
          />
        </div>

        {/* 3. Default Sender Radio */}
        <div className="space-y-1.5 pt-1 border-t border-slate-100">
          <label className="text-[10px] font-bold text-slate-500 uppercase block">메일 작성 시 기본 발신 계정</label>
          
          <div className="space-y-1.5">
            <label className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer ${
              defaultSender === currentUser?.Email
                ? 'bg-indigo-50/60 border-indigo-600'
                : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="mobile_default_sender"
                  checked={defaultSender === currentUser?.Email}
                  onChange={() => setDefaultSender(currentUser?.Email)}
                  className="w-3.5 h-3.5 text-indigo-600"
                />
                <span className="text-xs font-bold text-slate-900">{currentUser?.Email}</span>
              </div>
              {defaultSender === currentUser?.Email && (
                <span className="text-[9px] font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">기본</span>
              )}
            </label>

            {parsedAliasesList.map((aliasEmail, idx) => (
              <label key={idx} className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer ${
                defaultSender === aliasEmail
                  ? 'bg-indigo-50/60 border-indigo-600'
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="mobile_default_sender"
                    checked={defaultSender === aliasEmail}
                    onChange={() => setDefaultSender(aliasEmail)}
                    className="w-3.5 h-3.5 text-indigo-600"
                  />
                  <span className="text-xs font-bold text-slate-900">{aliasEmail}</span>
                </div>
                {defaultSender === aliasEmail && (
                  <span className="text-[9px] font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">기본</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 shadow-2xs"
        >
          <Save className="w-3.5 h-3.5" />
          <span>별칭 및 발신 설정 저장</span>
        </button>
      </form>

      {/* Password Change Card */}
      <form onSubmit={handlePasswordSubmit} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs space-y-3">
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">비밀번호 변경</span>
        
        {pwMsg && (
          <div className="p-2.5 bg-indigo-50 border border-indigo-200 rounded-xl text-xs font-bold text-indigo-800">
            {pwMsg}
          </div>
        )}

        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-500">새 비밀번호 (8자 이상)</label>
          <input
            type="password"
            placeholder="••••••••"
            value={newPw}
            onChange={e => setNewPw(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
            required
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-500">새 비밀번호 확인</label>
          <input
            type="password"
            placeholder="••••••••"
            value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
            required
          />
        </div>

        <button
          type="submit"
          className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs"
        >
          비밀번호 변경
        </button>
      </form>

      {/* Logout Button */}
      <button
        type="button"
        onClick={logout}
        className="w-full py-3 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 font-bold rounded-2xl text-xs flex items-center justify-center gap-1.5 shadow-2xs"
      >
        <LogOut className="w-4 h-4" />
        <span>로그아웃</span>
      </button>
    </div>
  );
};

export default MobileSettingsTab;
